// src/components/Toast.js
// Reusable toast notification system for Bracketz
// Usage:
//   import { useToast, ToastContainer } from './Toast';
//   const { toasts, showToast } = useToast();
//   showToast('Bracket submitted!', 'success');
//   showToast('Submission failed. Try again.', 'error');
//   showToast('Saving...', 'info');
//   <ToastContainer toasts={toasts} />

import React, { useState, useCallback } from 'react';

export function useToast() {
  const [toasts, setToasts] = useState([]);

  const showToast = useCallback((message, type = 'info', duration = 3500) => {
    const id = Date.now() + Math.random();
    setToasts(prev => [...prev, { id, message, type }]);
    setTimeout(() => {
      setToasts(prev => prev.filter(t => t.id !== id));
    }, duration);
  }, []);

  const removeToast = useCallback((id) => {
    setToasts(prev => prev.filter(t => t.id !== id));
  }, []);

  return { toasts, showToast, removeToast };
}

const ICONS = {
  success: '✓',
  error: '✕',
  info: 'ℹ',
  warning: '⚠',
};

const COLORS = {
  success: { bg: '#1a3a1a', border: '#2ecc71', icon: '#2ecc71', text: '#d4f5d4' },
  error:   { bg: '#3a1a1a', border: '#e74c3c', icon: '#e74c3c', text: '#f5d4d4' },
  info:    { bg: '#1a1a3a', border: '#E8630A', icon: '#E8630A', text: '#f5e6d4' },
  warning: { bg: '#3a2a1a', border: '#f39c12', icon: '#f39c12', text: '#f5ecd4' },
};

function Toast({ toast, onRemove }) {
  const c = COLORS[toast.type] || COLORS.info;
  return (
    <div
      onClick={() => onRemove(toast.id)}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
        background: c.bg,
        border: `1px solid ${c.border}`,
        borderRadius: '8px',
        padding: '12px 16px',
        marginBottom: '10px',
        cursor: 'pointer',
        animation: 'toastIn 0.25s ease',
        maxWidth: '340px',
        width: '100%',
        boxShadow: '0 4px 12px rgba(0,0,0,0.4)',
      }}
    >
      <span style={{
        width: '22px',
        height: '22px',
        borderRadius: '50%',
        background: c.border,
        color: '#fff',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: '13px',
        fontWeight: 'bold',
        flexShrink: 0,
      }}>
        {ICONS[toast.type]}
      </span>
      <span style={{
        color: c.text,
        fontSize: '14px',
        lineHeight: '1.4',
        flex: 1,
      }}>
        {toast.message}
      </span>
    </div>
  );
}

export function ToastContainer({ toasts, removeToast }) {
  if (!toasts || toasts.length === 0) return null;
  return (
    <>
      <style>{`
        @keyframes toastIn {
          from { opacity: 0; transform: translateY(16px); }
          to   { opacity: 1; transform: translateY(0); }
        }
      `}</style>
      <div style={{
        position: 'fixed',
        bottom: '80px',
        left: '50%',
        transform: 'translateX(-50%)',
        zIndex: 9999,
        display: 'flex',
        flexDirection: 'column-reverse',
        alignItems: 'center',
        pointerEvents: 'none',
        width: '100%',
        maxWidth: '360px',
        padding: '0 16px',
      }}>
        {toasts.map(t => (
          <div key={t.id} style={{ pointerEvents: 'all', width: '100%' }}>
            <Toast toast={t} onRemove={removeToast} />
          </div>
        ))}
      </div>
    </>
  );
}
